/**
 * Hook for managing column-level filter state
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { DEFAULT_COLUMN_FILTERS } from "../constants";
import {
  deriveFilterOptions,
  applyColumnFilters,
  getComparator,
} from "../utils";

/**
 * Manage column filter state for tables
 * @param {Array} rows - Data rows to filter
 * @param {string} defaultOrderBy - Default sort column
 * @param {string} defaultOrder - Default sort order
 * @returns {Object} Column filter state and handlers
 */
export const useColumnFilters = (
  rows,
  defaultOrderBy = "aquifer_name",
  defaultOrder = "asc"
) => {
  const [columnFilters, setColumnFilters] = useState(DEFAULT_COLUMN_FILTERS);
  const [colFiltersInitialized, setColFiltersInitialized] = useState(false);
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [order, setOrder] = useState(defaultOrder);

  // Derive available filter options from incoming rows
  const filterOptions = useMemo(() => deriveFilterOptions(rows), [rows]);

  // Initialize column filters when data changes
  useEffect(() => {
    if (filterOptions.years.length > 0) {
      setColumnFilters({
        years: [...filterOptions.years],
        aquifers: [...filterOptions.aquifers],
        aggSystems: [...filterOptions.aggSystems],
        wells: [...filterOptions.wells],
        stateWells: [...filterOptions.stateWells],
        wellNames: [...filterOptions.wellNames],
      });
      setColFiltersInitialized(true);
    }
  }, [filterOptions]);

  const handleFilterChange = useCallback((key, values) => {
    setColumnFilters((prev) => ({ ...prev, [key]: values }));
  }, []);

  const handleSort = useCallback(
    (columnId) => {
      setOrder((prevOrder) => {
        if (orderBy === columnId && prevOrder === "asc") {
          return "desc";
        }
        return "asc";
      });
      setOrderBy(columnId);
    },
    [orderBy]
  );

  // Apply column filters then sort
  const filteredAndSortedRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const filtered = applyColumnFilters(
      rows,
      columnFilters,
      colFiltersInitialized
    );

    if (!colFiltersInitialized) {
      return [...rows].sort(getComparator(order, orderBy));
    }

    return filtered.sort(getComparator(order, orderBy));
  }, [rows, columnFilters, order, orderBy, colFiltersInitialized]);

  return {
    columnFilters,
    filterOptions,
    colFiltersInitialized,
    orderBy,
    order,
    handleFilterChange,
    handleSort,
    filteredAndSortedRows,
  };
};
